import { useEffect, useId, useState } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Bell,
  Building2,
  CalendarDays,
  CheckSquare,
  ChevronDown,
  ClipboardList,
  Folder,
  Home,
  LifeBuoy,
  Megaphone,
  MessageSquare,
  MessagesSquare,
  Settings,
  Star,
  type LucideIcon,
} from 'lucide-react';
import { activityApi, projectsApi } from '@/lib/api';
import { cn } from '@/lib/utils';
import { discussionLabels } from '@/lib/discussion-labels';
import { groupByOrg, pushRecentProject } from '@/lib/project-groups';
import { useAuthStore, getIsAgencyAdmin, getIsAgencyMember } from '@/stores/auth.store';
import { useIsAgencyPerson } from '@/hooks/useIsAgencyPerson';
import { useCanFavorite } from '@/hooks/useCanFavorite';
import { ProjectSwitcher } from './ProjectSwitcher';

/** AppShell's own nav item look, so these read as the same nav. */
const ITEM = 'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-200';
const ITEM_ACTIVE = 'bg-secondary text-white shadow-card';
const ITEM_IDLE = 'text-secondary-600 hover:bg-grey-100 hover:text-secondary';
const CAPTION = 'mb-1 px-3 text-caption font-bold uppercase tracking-wider text-grey-400';

/**
 * A nav section that folds up to its heading (Jesse, 2026-10-21: Favorites and
 * Agency "both expandable/minimizable"). Remembered per browser; storage can
 * throw (private window, blocked site data), and then it just starts open.
 */
function useCollapsed(section: string): [boolean, () => void] {
  const key = `solid-hub-nav-collapsed:${section}`;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(key) === '1';
    } catch {
      return false;
    }
  });
  const toggle = () =>
    setCollapsed((was) => {
      try {
        localStorage.setItem(key, was ? '0' : '1');
      } catch {
        // not remembered, still toggles
      }
      return !was;
    });
  return [collapsed, toggle];
}

/** The section's caption, as the button that folds it. */
function SectionToggle({
  label,
  collapsed,
  onToggle,
  controls,
  count,
  className,
}: {
  label: string;
  collapsed: boolean;
  onToggle: () => void;
  controls: string;
  /** Shown only while folded, so an unread count isn't hidden with its item. */
  count?: number;
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={!collapsed}
      aria-controls={controls}
      className={cn(CAPTION, 'flex w-full items-center gap-1.5 rounded-md py-0.5 text-left hover:text-secondary', className)}
    >
      <span className="flex-1">{label}</span>
      {collapsed && !!count && (
        <span className="num min-w-[18px] rounded-full bg-primary px-1 text-center text-[10px] font-bold leading-[18px] tracking-normal text-white">
          {count > 99 ? '99+' : count}
          <span className="sr-only"> unread</span>
        </span>
      )}
      <ChevronDown className={cn('h-3.5 w-3.5 shrink-0 transition-transform', collapsed && '-rotate-90')} aria-hidden />
    </button>
  );
}

interface Item {
  to: string;
  label: string;
  icon: LucideIcon;
  end?: boolean;
  /** Extra paths that count as this item (e.g. the task board under Tasks). */
  alsoActive?: string[];
  /** Unread count, shown as a badge (Activity). */
  count?: number;
}

/**
 * The left nav, Sites-style (Jesse, 2026-10-07), top to bottom:
 *   - the organization and project dropdowns (with search)
 *   - the open project's screens
 *   - your favorite projects, grouped by organization (agency people; also
 *     behind the ★ on the app bar)
 *   - pinned to the bottom: the Agency section (My tasks and Activity for
 *     agency people, plus the staff tools), then Support and Settings
 *     (everyone)
 * It replaces the old global nav and the project's horizontal tabs, so a
 * project can grow more screens without the tab bar running out of room.
 *
 * The app bar carries only the Favorites ★ and the chat applet.
 */
export function ProjectNav({ onNavigate }: { onNavigate: () => void }) {
  const { pathname } = useLocation();
  const projectId = pathname.match(/^\/projects\/([^/]+)/)?.[1];

  // Where "/" lands next time, and which project an org switch opens
  useEffect(() => {
    if (projectId) pushRecentProject(projectId);
  }, [projectId]);

  // Every link closes the mobile drawer; delegated so no link needs a handler
  const closeOnLink = (e: React.MouseEvent<HTMLElement>) => {
    if ((e.target as HTMLElement).closest('a')) onNavigate();
  };

  return (
    <div className="flex min-h-0 flex-1 flex-col" onClick={closeOnLink}>
      <div className="shrink-0 px-3 pt-4">
        <ProjectSwitcher currentProjectId={projectId} onNavigate={onNavigate} />
      </div>
      {projectId && <ProjectSections projectId={projectId} />}
      {/* Bound to the bottom (Jesse, 2026-10-21): Favorites sit right on top
          of Agency, and the spare room opens above them. Only Favorites
          shrink (and scroll); the block under them never moves. */}
      <div className="mt-auto flex min-h-0 flex-col bg-white">
        <FavoritesList currentProjectId={projectId} />
        <div className="shrink-0">
          <AgencyNav />
          <PersonalNav />
        </div>
      </div>
    </div>
  );
}

function NavItems({ items }: { items: Item[] }) {
  const { pathname } = useLocation();
  return (
    <ul className="space-y-0.5">
      {items.map((item) => (
        <li key={item.to}>
          <NavLink
            to={item.to}
            end={item.end}
            className={({ isActive }) =>
              cn(ITEM, isActive || item.alsoActive?.some((p) => pathname.startsWith(p)) ? ITEM_ACTIVE : ITEM_IDLE)
            }
          >
            <item.icon className="h-4 w-4 shrink-0" aria-hidden />
            <span className="min-w-0 flex-1 truncate">{item.label}</span>
            {!!item.count && (
              <span className="num min-w-[20px] rounded-full bg-primary px-1.5 text-center text-[11px] font-bold leading-5 text-white">
                {item.count > 99 ? '99+' : item.count}
                <span className="sr-only"> unread</span>
              </span>
            )}
          </NavLink>
        </li>
      ))}
    </ul>
  );
}

/** The open project's screens: what used to be its tabs. */
function ProjectSections({ projectId }: { projectId: string }) {
  const { data: project } = useQuery({
    queryKey: ['project', projectId],
    queryFn: () => projectsApi.get(projectId),
  });
  const base = `/projects/${projectId}`;

  const customTypes: Array<{ slug: string; label: string; labelPlural?: string; position?: number }> = (
    (project?.settings as any)?.discussionTypes ?? []
  )
    .slice()
    .sort((a: any, b: any) => (a.position ?? 0) - (b.position ?? 0));

  const items: Item[] = [
    { to: base, label: 'Overview', icon: Home, end: true },
    { to: `${base}/discussions`, label: discussionLabels(project?.settings).plural, icon: MessageSquare },
    ...customTypes.map((t) => ({ to: `${base}/custom/${t.slug}`, label: t.labelPlural || t.label, icon: MessagesSquare })),
    { to: `${base}/tasks`, label: 'Tasks', icon: CheckSquare, alsoActive: [`${base}/board`] },
    { to: `${base}/events`, label: 'Schedule', icon: CalendarDays },
    { to: `${base}/files`, label: 'Files', icon: Folder },
    { to: `${base}/forms`, label: 'Forms', icon: ClipboardList },
  ];

  return (
    <nav aria-label="Project" className="shrink-0 px-3 py-4">
      <NavItems items={items} />
    </nav>
  );
}

/**
 * Your favorite projects, grouped by organization, A–Z, below the open
 * project's screens. Back in the nav after a spell as only the app bar ★
 * (Jesse, 2026-10-21: "I still want a list of pinned projects in my left
 * nav"); the ★ panel stays too. Agency people only, like every favorite
 * (useCanFavorite). The project you're in gets a light wash, not the navy
 * pill, so it never competes with the screen you're on.
 *
 * Compact, and the one part of the nav that scrolls (Jesse, 2026-10-21): the
 * project's screens above and the Agency/Support/Settings block below stay
 * put while a long list scrolls inside its own box. It's bound to the bottom,
 * on top of Agency, sized to its content until the column runs out of room.
 * A list long enough to scroll gets a 10rem floor on the section (not the
 * list: with min-h-0 there, a short window squeezed it to nothing under
 * Agency); a short one never shrinks. Past the floor the column scrolls.
 */
function FavoritesList({ currentProjectId }: { currentProjectId?: string }) {
  const canFavorite = useCanFavorite();
  const [collapsed, toggle] = useCollapsed('favorites');
  const listId = useId();
  const { data: projects } = useQuery({
    queryKey: ['projects', 'member'],
    queryFn: () => projectsApi.list(),
    enabled: canFavorite,
  });
  const live = (projects ?? []).filter((p: any) => !p.archivedAt);
  const groups = groupByOrg(live.filter((p: any) => p.isFavorited));
  const favoriteCount = groups.reduce((n, g) => n + g.projects.length, 0);
  // One project: nothing to switch to, so no favorites to keep
  if (!canFavorite || live.length <= 1) return null;

  return (
    <nav
      aria-label="Favorites"
      className={cn(
        'flex flex-col border-t border-grey-200 py-4',
        collapsed ? 'shrink-0' : cn('pb-0', favoriteCount <= 3 ? 'shrink-0' : 'min-h-[10rem]'),
      )}
    >
      <div className="shrink-0 px-3">
        <SectionToggle label="Favorites" collapsed={collapsed} onToggle={toggle} controls={listId} />
      </div>
      {collapsed ? null : groups.length === 0 ? (
        <p id={listId} className="flex items-start gap-1.5 px-6 pb-4 pt-1 text-sm text-grey-500">
          <Star className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
          Star a project to keep it here.
        </p>
      ) : (
        <div id={listId} className="min-h-0 space-y-1.5 overflow-y-auto overscroll-contain px-3 pb-3">
          {groups.map((g) => (
            <div key={g.orgId}>
              <p className="truncate px-3 text-[10px] font-semibold uppercase leading-4 tracking-wider text-grey-400">
                {g.orgName}
              </p>
              <ul>
                {g.projects.map((p: any) => {
                  const isCurrent = p.id === currentProjectId;
                  return (
                    <li key={p.id}>
                      <NavLink
                        to={`/projects/${p.id}`}
                        aria-current={isCurrent ? 'page' : undefined}
                        className={cn(
                          'block truncate rounded-md px-3 py-0.5 text-[13px] leading-5 transition-colors',
                          isCurrent ? 'bg-grey-100 font-semibold text-secondary' : ITEM_IDLE,
                        )}
                      >
                        {p.name}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </div>
      )}
    </nav>
  );
}

/**
 * The Agency section, pinned to the bottom of the nav (Jesse, 2026-10-07):
 * the cross-project screens that aren't about one project.
 *   - My tasks and Activity, for every agency person (Solid staff and
 *     partner-agency staff). A client works in their project instead: its
 *     Tasks screen shows what's assigned to them, and its Overview has an
 *     Activity card. Activity left the app bar for here and keeps its count.
 *   - Announcements (admins), Forms and Organizations, for Solid staff; each
 *     keeps the gate its screen already has.
 */
function AgencyNav() {
  const { user } = useAuthStore();
  const isAgencyPerson = useIsAgencyPerson();
  const isStaff = getIsAgencyMember(user);
  const isAdmin = getIsAgencyAdmin(user);
  const unread = useActivityUnread(isAgencyPerson);
  const [collapsed, toggle] = useCollapsed('agency');
  const listId = useId();
  if (!isAgencyPerson) return null;

  const items: Item[] = [
    { to: '/tasks', label: 'My tasks', icon: CheckSquare },
    { to: '/activity', label: 'Activity', icon: Bell, count: unread },
    ...(isAdmin ? [{ to: '/announcements', label: 'Announcements', icon: Megaphone }] : []),
    ...(isStaff
      ? [
          { to: '/forms/manage', label: 'Forms', icon: ClipboardList },
          { to: '/organizations', label: 'Organizations', icon: Building2 },
        ]
      : []),
  ];

  return (
    <nav aria-label="Agency" className={cn('border-t border-grey-200 px-3', collapsed ? 'py-3' : 'py-4')}>
      <SectionToggle
        label="Agency"
        collapsed={collapsed}
        onToggle={toggle}
        controls={listId}
        count={unread}
        className={collapsed ? 'mb-0' : undefined}
      />
      {!collapsed && (
        <div id={listId}>
          <NavItems items={items} />
        </div>
      )}
    </nav>
  );
}

/**
 * Last in the nav, for everyone: Support (the /help page) and Settings, which
 * both left the app bar for here (Jesse, 2026-10-07). Settings shows each
 * person the tabs their role allows.
 */
function PersonalNav() {
  const items: Item[] = [
    { to: '/help', label: 'Support', icon: LifeBuoy },
    { to: '/settings', label: 'Settings', icon: Settings },
  ];
  return (
    <nav aria-label="You" className="border-t border-grey-200 px-3 py-3">
      <NavItems items={items} />
    </nav>
  );
}

/** Unread Activity; the socket store invalidates this query, so it moves live. */
function useActivityUnread(enabled: boolean) {
  const { data } = useQuery({
    queryKey: ['activity-unread-count'],
    queryFn: () => activityApi.getUnreadCount(),
    enabled,
    refetchInterval: 60_000,
  });
  return data?.count ?? 0;
}
